import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { AlertController, NavController } from '@ionic/angular';
import { AuthStateService } from '../app/auth/auth-state.service';
import { PrivilegeName } from '../models/privileges';

/**
 * Guard that checks if the current user has the required privilege to access a route.
 * Usage in route config:
 * {
 *   path: 'admin/users',
 *   loadChildren: ...,
 *   canActivate: [PrivilegeGuard],
 *   data: { privilege: 'users_admin' }
 * }
 */
@Injectable({
    providedIn: 'root'
})
export class PrivilegeGuard implements CanActivate {

    constructor(
        private navController: NavController,
        private authStateService: AuthStateService,
        private alertController: AlertController
    ) {}

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): boolean {
        const requiredPrivilege = route.data.privilege as PrivilegeName | undefined;

        if (!requiredPrivilege) {
            // No privilege required, allow access
            return true;
        }

        // AuthGuard runs before this guard and waits for auth state to be loaded,
        // so by the time we get here, the user should already be set in AuthStateService
        const user = this.authStateService.user();

        if (!user) {
            // This shouldn't happen if AuthGuard is configured correctly
            this.navController.navigateRoot(['/auth']);
            return false;
        }

        const hasPrivilege = user.hasPrivilege(requiredPrivilege);

        if (!hasPrivilege) {
            // User doesn't have the required privilege, redirect to dashboard
            this.showNoAccessAlert();
            this.navController.navigateRoot(['/dashboard']);
            return false;
        }

        return true;
    }

    private async showNoAccessAlert(): Promise<void> {
        const alert = await this.alertController.create({
            header: 'Geen toegang',
            message: 'Je hebt geen rechten om deze pagina te bekijken.',
            buttons: ['OK']
        });
        await alert.present();
    }
}
